import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { DumbbellIcon, ScaleIcon, ZapIcon, HeartIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, CheckIcon, ShoppingCartIcon } from 'lucide-react';
type Goal = 'muscle' | 'weight' | 'performance' | 'health';
interface Supplement {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  goals: Goal[];
}
const mockSupplements: Supplement[] = [{
  id: '1',
  name: '100% Whey Protein Isolate',
  description: 'Fast-absorbing protein for muscle recovery',
  price: 249,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['muscle', 'performance']
}, {
  id: '2',
  name: 'Creatine Monohydrate',
  description: 'Increase strength and power output',
  price: 129,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['muscle', 'performance']
}, {
  id: '3',
  name: 'Thermogenic Fat Burner',
  description: 'Boost metabolism and energy',
  price: 189,
  image: 'https://drnutrition.com/storage/media/uksBiPpW7gAOFEDeDTmu50CbihxIWLQKjiOeSqtu.jpg',
  goals: ['weight']
}, {
  id: '4',
  name: 'L-Carnitine Liquid',
  description: 'Convert fat into energy',
  price: 149,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['weight', 'performance']
}, {
  id: '5',
  name: 'Pre-Workout Explosive',
  description: 'Intense energy and focus',
  price: 199,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['performance', 'muscle']
}, {
  id: '6',
  name: 'Daily Multivitamin',
  description: 'Essential vitamins and minerals',
  price: 99,
  image: 'https://drnutrition.com/storage/media/uksBiPpW7gAOFEDeDTmu50CbihxIWLQKjiOeSqtu.jpg',
  goals: ['health', 'muscle', 'weight', 'performance']
}, {
  id: '7',
  name: 'Omega-3 Fish Oil',
  description: 'Heart and joint health support',
  price: 119,
  image: 'https://drnutrition.com/storage/media/dnXydbj2uuEivTxr3oVA80E9Z07y2yoOeiJTpnsE.jpg',
  goals: ['health', 'weight']
}, {
  id: '8',
  name: 'BCAA Recovery',
  description: 'Intra-workout muscle preservation',
  price: 159,
  image: 'https://drnutrition.com/storage/media/prlvW98QxVHRvalrZxM4NppiQeglKJpljsFMdDGz.jpg',
  goals: ['muscle', 'performance']
}];
export function BundleBuilderPage() {
  const [step, setStep] = useState(1);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [selectedSupplements, setSelectedSupplements] = useState<string[]>([]);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  const goals = [{
    id: 'muscle' as Goal,
    title: 'Muscle Building',
    description: 'Gain size and strength',
    icon: <DumbbellIcon className="w-8 h-8" data-id="element-6174" data-mp="3hrex7vkwtgbe" />,
    emoji: '💪'
  }, {
    id: 'weight' as Goal,
    title: 'Weight Loss',
    description: 'Burn fat and get lean',
    icon: <ScaleIcon className="w-8 h-8" data-id="element-6175" data-mp="1p2xnrtuu0z4x" />,
    emoji: '🔥'
  }, {
    id: 'performance' as Goal,
    title: 'Performance',
    description: 'Boost energy and endurance',
    icon: <ZapIcon className="w-8 h-8" data-id="element-6176" data-mp="1g30ufexhzc3j" />,
    emoji: '⚡'
  }, {
    id: 'health' as Goal,
    title: 'General Health',
    description: 'Overall wellness and immunity',
    icon: <HeartIcon className="w-8 h-8" data-id="element-6177" data-mp="27tylyfo4oqnf" />,
    emoji: '❤️'
  }];
  const filteredSupplements = mockSupplements.filter(s => selectedGoal ? s.goals.includes(selectedGoal) : true);
  const toggleSupplement = (id: string) => {
    setSelectedSupplements(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  const originalTotal = selectedSupplements.reduce((total, id) => {
    const supp = mockSupplements.find(s => s.id === id);
    return total + (supp?.price || 0);
  }, 0);
  const savings = originalTotal * 0.2;
  const discountedTotal = originalTotal - savings;
  const handleAddToCart = () => {
    toast.success('Bundle added to cart successfully!', {
      description: `You saved SAR ${savings.toFixed(2)} on this bundle.`,
      icon: <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-6178" data-mp="r9obz40qda6i" />
    });
    setStep(1);
    setSelectedGoal(null);
    setSelectedSupplements([]);
  };
  return <HelmetProvider data-id="element-6179" data-mp="w7zh51g2ywie">
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-6180" data-mp="w6oi1exkycxm">
        <SEO title="Bundle Builder | Innova Pharmacy" description="Build your custom supplement bundle and save 20%." data-id="element-6181" data-mp="116kjp7it1sa0" />

        <div className="hidden md:block" data-id="element-6182" data-mp="1a05ygquyz9jf">
          <AnnouncementBar data-id="element-6183" data-mp="19yvytx8gxtao" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-6184" data-mp="1a2a2d8svc8jp" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6185" data-mp="1jxk21pvso3ub" />
        </div>

        <div className="fixed inset-x-0 top-0 z-50 md:hidden" data-id="element-6186" data-mp="3b0kbtc69fjcj">
          <MobileInternalHeader title="Bundle Builder" data-id="element-6187" data-mp="3ps30rzh3ko81" />
        </div>

        <div className="pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-6188" data-mp="3l02y3z9n7h7r">
          <PageHeroBreadcrumb title="Bundle Builder" description="Build your custom supplement bundle and save 20%." breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Bundle Builder'
        }]} data-id="element-6189" data-mp="2mli6h6rga27" />
        </div>

        <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl" data-id="element-6190" data-mp="7mhl0d3q1vg6">
          {/* Progress Bar */}
          <div className="mb-8" data-id="element-6191" data-mp="2ee8o7shhnyx2">
            <div className="flex justify-between mb-2" data-id="element-6192" data-mp="29dbabs3ihr1d">
              <span className={`text-sm font-medium ${step >= 1 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6193" data-mp="29ew1d7qmaruh">
                1. Goal
              </span>
              <span className={`text-sm font-medium ${step >= 2 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6194" data-mp="tssioser1l3c">
                2. Supplements
              </span>
              <span className={`text-sm font-medium ${step >= 3 ? 'text-[#1E2B55]' : 'text-gray-400'}`} data-id="element-6195" data-mp="k38xvh7h7h5u">
                3. Review
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-6196" data-mp="b4nke0gv2sbx">
              <motion.div className="h-full bg-[#1E2B55]" initial={{
              width: '33%'
            }} animate={{
              width: `${step / 3 * 100}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-6197" data-mp="3sc9362q2c6mh" />
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-6198" data-mp="ja42sxczwke9">
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-6199" data-mp="j9arrk7078qb">
                <div className="text-center mb-8" data-id="element-6200" data-mp="3vpeftg4rzioc">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-6201" data-mp="3rouj2do46h2q">
                    Choose Your Goal
                  </h2>
                  <p className="text-gray-600" data-id="element-6202" data-mp="3msx4ocwevksl">
                    Select your primary fitness goal to see recommended
                    supplements.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6203" data-mp="1kxd86siv6wup">
                  {goals.map(goal => <button key={goal.id} onClick={() => {
                setSelectedGoal(goal.id);
                setStep(2);
              }} className={`rounded-2xl border-2 p-6 text-left transition-[border-color,background-color,box-shadow] duration-200 ${selectedGoal === goal.id ? 'border-brand bg-brand-light' : 'border-gray-200 bg-white hover:border-sage-border hover:shadow-md'}`} data-id="element-6204" data-mp="1njvsj8xl8pm7">
                      <div className="text-4xl mb-4" data-id="element-6205" data-mp="19k4kdotr63g4">{goal.emoji}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-6206" data-mp="19kcwhei1x4q9">
                        {goal.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-6207" data-mp="1ji0p9yw3k25s">{goal.description}</p>
                    </button>)}
                </div>
              </motion.div>}

            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6 pb-32" data-id="element-6208" data-mp="2uwo4ovrjmwtm">
                <div className="flex items-center justify-between mb-6" data-id="element-6209" data-mp="1u7bdxhndp4pt">
                  <button onClick={() => setStep(1)} className="flex items-center text-gray-600 hover:text-[#1E2B55]" data-id="element-6210" data-mp="xilsjhcjltz3">
                    <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-6211" data-mp="16is24s54e9sz" />
                    Back
                  </button>
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6212" data-mp="1bhlkaxvcqozh">
                    Select Supplements
                  </h2>
                  <div className="w-20" data-id="element-6213" data-mp="2hwvy23v9q80o"></div> {/* Spacer for centering */}
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex items-start gap-3" data-id="element-6214" data-mp="2mvz3vqj2oi9p">
                  <div className="bg-blue-100 p-2 rounded-full text-blue-600 mt-0.5" data-id="element-6215" data-mp="b8dh6vxpi4zf">
                    <ZapIcon className="w-5 h-5" data-id="element-6216" data-mp="6bmzy5a613cf" />
                  </div>
                  <div data-id="element-6217" data-mp="1jdccvwiwfdb">
                    <h4 className="font-semibold text-blue-900" data-id="element-6218" data-mp="1itx5my9c52z">
                      Bundle & Save 20%
                    </h4>
                    <p className="text-sm text-blue-700" data-id="element-6219" data-mp="3spwnyda6o9co">
                      Select at least 2 supplements to unlock your bundle
                      discount.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6220" data-mp="3eq5bwi38931b">
                  {filteredSupplements.map(supp => {
                const isSelected = selectedSupplements.includes(supp.id);
                return <div key={supp.id} onClick={() => toggleSupplement(supp.id)} className={`relative flex cursor-pointer gap-4 rounded-xl border-2 p-4 transition-[border-color,background-color] duration-200 ${isSelected ? 'border-brand bg-brand-light' : 'border-gray-200 bg-white hover:border-sage-border'}`} data-id="element-6221" data-mp="1cxqxvu3t0iq6">
                        <div className="w-20 h-20 bg-white rounded-lg border border-gray-100 p-2 flex-shrink-0" data-id="element-6222" data-mp="2sjkrco2gbmco">
                          <img src={supp.image} alt={supp.name} className="w-full h-full object-contain" data-id="element-6223" data-mp="2xhcihfv9v4ft" />
                        </div>
                        <div className="flex-grow" data-id="element-6224" data-mp="3c6ihb2q76sn6">
                          <h3 className="font-bold text-gray-900 mb-1 leading-tight" data-id="element-6225" data-mp="3c5pegcud0nab">
                            {supp.name}
                          </h3>
                          <p className="text-sm text-gray-500 mb-2 line-clamp-2" data-id="element-6226" data-mp="20qkaz6memkpn">
                            {supp.description}
                          </p>
                          <div className="font-bold text-[#1E2B55]" data-id="element-6227" data-mp="24q2npf42nphe">
                            SAR {supp.price.toFixed(2)}
                          </div>
                        </div>
                        <div className="absolute top-4 right-4" data-id="element-6228" data-mp="1sbw6gia0vnh1">
                          <div className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${isSelected ? 'border-brand bg-brand' : 'border-gray-300'}`} data-id="element-6229" data-mp="275r936jl4gjs">
                            {isSelected && <CheckIcon className="w-4 h-4 text-white" data-id="element-6230" data-mp="19jtwfprvz5fr" />}
                          </div>
                        </div>
                      </div>;
              })}
                </div>

                {/* Floating Summary Bar */}
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] p-4 z-40" data-id="element-6231" data-mp="vrdoqbo1ij8i">
                  <div className="container mx-auto max-w-4xl flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-6232" data-mp="qskaui4ucq7t">
                    <div className="flex-grow flex items-center justify-between sm:justify-start sm:gap-8 w-full sm:w-auto" data-id="element-6233" data-mp="3q50bkr7bojke">
                      <div data-id="element-6234" data-mp="31lp8aoqeurq">
                        <div className="text-sm text-gray-500" data-id="element-6235" data-mp="31c0srjc4vo5">
                          Selected Items
                        </div>
                        <div className="font-bold text-lg" data-id="element-6236" data-mp="7uon2v0jkqdq">
                          {selectedSupplements.length}
                        </div>
                      </div>
                      {selectedSupplements.length >= 2 && <>
                          <div data-id="element-6237" data-mp="ltwntsccs6k1">
                            <div className="text-sm text-gray-500" data-id="element-6238" data-mp="lqscmb0h0ajv">
                              Original
                            </div>
                            <div className="font-medium text-gray-400 line-through" data-id="element-6239" data-mp="2nkrniv9okpw1">
                              SAR {originalTotal.toFixed(2)}
                            </div>
                          </div>
                          <div data-id="element-6240" data-mp="2l0li0j5f4rdd">
                            <div className="text-sm text-green-600 font-medium" data-id="element-6241" data-mp="2l0v1rozve2am">
                              Bundle Total
                            </div>
                            <div className="font-bold text-xl text-green-600" data-id="element-6242" data-mp="2b8z2iam1s64t">
                              SAR {discountedTotal.toFixed(2)}
                            </div>
                          </div>
                        </>}
                    </div>
                    <button onClick={() => setStep(3)} disabled={selectedSupplements.length < 2} className={`w-full sm:w-auto px-8 py-3 rounded-xl font-bold flex items-center justify-center transition-colors ${selectedSupplements.length >= 2 ? 'bg-[#1E2B55] text-white hover:bg-blue-900' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-6243" data-mp="2yzkyh3is1zyg">
                      Continue to Review
                      <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-6244" data-mp="1elucu399ikdj" />
                    </button>
                  </div>
                </div>
              </motion.div>}

            {step === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-6245" data-mp="1kxdn9knoio0v">
                <div className="flex items-center justify-between mb-6" data-id="element-6246" data-mp="nbzhmpk3egw3">
                  <button onClick={() => setStep(2)} className="flex items-center text-gray-600 hover:text-[#1E2B55]" data-id="element-6247" data-mp="n9v7vqziinnw">
                    <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-6248" data-mp="ebt97i39473s" />
                    Back
                  </button>
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-6249" data-mp="ic3ip4q95fne">
                    Review Bundle
                  </h2>
                  <div className="w-20" data-id="element-6250" data-mp="3quvq3nwz5s70"></div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-6251" data-mp="3qwqbf3c5rmbm">
                  <div className="p-6 border-b border-gray-100" data-id="element-6252" data-mp="8qu0cjv24ou4">
                    <h3 className="font-bold text-lg mb-4" data-id="element-6253" data-mp="3pwkwf1fm8wb">
                      Selected Supplements
                    </h3>
                    <div className="space-y-4" data-id="element-6254" data-mp="ilvtrrwd8d2i">
                      {selectedSupplements.map(id => {
                    const supp = mockSupplements.find(s => s.id === id);
                    if (!supp) return null;
                    const discountedPrice = supp.price * 0.8;
                    return <div key={supp.id} className="flex items-center gap-4" data-id="element-6255" data-mp="ngja33h7u160">
                            <div className="w-16 h-16 bg-gray-50 rounded-lg border border-gray-100 p-1 flex-shrink-0" data-id="element-6256" data-mp="wgeori9zceas">
                              <img src={supp.image} alt={supp.name} className="w-full h-full object-contain" data-id="element-6257" data-mp="23tdhwb4r9luh" />
                            </div>
                            <div className="flex-grow" data-id="element-6258" data-mp="2hpqx1dlthb5s">
                              <h4 className="font-semibold text-gray-900" data-id="element-6259" data-mp="1k3lb0ex7fcvb">
                                {supp.name}
                              </h4>
                            </div>
                            <div className="text-right" data-id="element-6260" data-mp="1u1hkz3eeo1ru">
                              <div className="text-sm text-gray-400 line-through" data-id="element-6261" data-mp="1tzwpnbk9oesx">
                                SAR {supp.price.toFixed(2)}
                              </div>
                              <div className="font-bold text-green-600" data-id="element-6262" data-mp="11bsi0ki4xrnl">
                                SAR {discountedPrice.toFixed(2)}
                              </div>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50" data-id="element-6263" data-mp="tu2quneuie2y">
                    <div className="space-y-3 mb-6" data-id="element-6264" data-mp="otezu1qm9x8q">
                      <div className="flex justify-between text-gray-600" data-id="element-6265" data-mp="owaw7lmtgdv2">
                        <span data-id="element-6266" data-mp="julify87lm3a">Original Total</span>
                        <span data-id="element-6267" data-mp="ft9t7tt8z2vm">SAR {originalTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-600 font-medium" data-id="element-6268" data-mp="b334zv2ndkdw">
                        <span data-id="element-6269" data-mp="63q6ue6bsqw0">Bundle Savings (20%)</span>
                        <span data-id="element-6270" data-mp="64i19lo6sikp">- SAR {savings.toFixed(2)}</span>
                      </div>
                      <div className="h-px bg-gray-200 my-2" data-id="element-6271" data-mp="15ygholbxctx"></div>
                      <div className="flex justify-between text-xl font-bold text-gray-900" data-id="element-6272" data-mp="3saopc1rhz6qk">
                        <span data-id="element-6273" data-mp="3o9lddqckluum">Final Total</span>
                        <span data-id="element-6274" data-mp="3o9v5bjh6ec2y">SAR {discountedTotal.toFixed(2)}</span>
                      </div>
                    </div>

                    <button onClick={handleAddToCart} className="w-full bg-[#1E2B55] text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-colors flex items-center justify-center" data-id="element-6275" data-mp="2d3snicvpx5f2">
                      <ShoppingCartIcon className="w-5 h-5 mr-2" data-id="element-6276" data-mp="1z5c69s08bwio" />
                      Add Bundle to Cart
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </main>

        <Footer data-id="element-6277" data-mp="1kgy65ou76m5t" />
        <MobileFooterNav data-id="element-6278" data-mp="1kgg3oagxzbpg" />
        <PharmacistChat data-id="element-6279" data-mp="1khr4q2j065cv" />

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={loc => {
        setSelectedLocation(loc);
        setLocationModalOpen(false);
      }} data-id="element-6280" data-mp="1kev76fb2hioy" />
      </div>
    </HelmetProvider>;
}